import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {LocalisationController} from "hooks/translation";

type ExpandOverride = {
  nonce: number;
  expanded: boolean;
};

type LocalisationContextValue = {
  controller: LocalisationController;
  registerSection: (id: string) => () => void;
  sectionCount: number;
  override?: ExpandOverride;
  anyExpanded: boolean;
  reportExpanded: (id: string, expanded: boolean) => void;
  toggleAll: () => void;
  hasBar: boolean;
  registerBar: () => () => void;
  /**
   * Target keys this surface's sections derive their default expanded state
   * from. Unset = every target of the controller; a nested surface (subtask
   * or grid modal) passes its own keys so it is judged in isolation.
   */
  scopeKeys?: string[];
};

const LocalisationContext = createContext<LocalisationContextValue | null>(
  null
);

type Props = {
  controller?: LocalisationController;
  /** See LocalisationContextValue.scopeKeys. Memoize at the call site. */
  scopeKeys?: string[];
};

const LocalisationProvider: React.FC<Props> = ({
  controller,
  scopeKeys,
  children,
}) => {
  const parent = useContext(LocalisationContext);
  const [sectionIds, setSectionIds] = useState<string[]>([]);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [override, setOverride] = useState<ExpandOverride | undefined>();
  const [barCount, setBarCount] = useState(0);

  const registerBar = useCallback(() => {
    setBarCount((prev) => prev + 1);

    return () => setBarCount((prev) => prev - 1);
  }, []);

  const registerSection = useCallback((id: string) => {
    setSectionIds((prev) => (prev.includes(id) ? prev : [...prev, id]));

    return () => {
      setSectionIds((prev) => prev.filter((item) => item !== id));
      setExpandedIds((prev) => prev.filter((item) => item !== id));
    };
  }, []);

  const reportExpanded = useCallback((id: string, expanded: boolean) => {
    setExpandedIds((prev) => {
      if (expanded) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter((item) => item !== id);
    });
  }, []);

  const anyExpanded = expandedIds.length > 0;

  const toggleAll = useCallback(() => {
    setOverride((prev) => ({
      nonce: (prev?.nonce ?? 0) + 1,
      expanded: expandedIds.length === 0,
    }));
  }, [expandedIds.length]);

  const inheritedController = controller ?? parent?.controller;

  const value = useMemo(
    () => ({
      controller: inheritedController as LocalisationController,
      registerSection,
      sectionCount: sectionIds.length,
      override,
      anyExpanded,
      reportExpanded,
      toggleAll,
      hasBar: barCount > 0,
      registerBar,
      scopeKeys,
    }),
    [
      inheritedController,
      registerSection,
      sectionIds.length,
      override,
      anyExpanded,
      reportExpanded,
      toggleAll,
      barCount,
      registerBar,
      scopeKeys,
    ]
  );

  if (!inheritedController) {
    throw new Error(
      "<LocalisationProvider> needs a controller, or a parent provider to inherit one from"
    );
  }

  return (
    <LocalisationContext.Provider value={value}>
      {children}
    </LocalisationContext.Provider>
  );
};

export const useLocalisationContext = (): LocalisationContextValue => {
  const context = useContext(LocalisationContext);

  if (!context) {
    throw new Error(
      "Localisation components must be rendered inside <LocalisationProvider>"
    );
  }

  return context;
};

export const useOptionalLocalisationContext = (): LocalisationContextValue | null =>
  useContext(LocalisationContext);

export const useHasLocalisationContext = (): boolean =>
  useContext(LocalisationContext) !== null;

export default LocalisationProvider;
